'use strict';

const _ = require('lodash');
const moment = require('moment');

const getSales = ({ volume, price }) => {
  return volume * price || 0;
};

const getMarginTotal = ({ marginPercentage, sales }) => {
  return (marginPercentage / 100) * sales || 0;
};

const getMarginPercentage = ({ marginTotal, sales }) => {
  return (marginTotal / sales) * 100 || 0;
};

/**
 * Calculate weighted average price from price history based on the last year
 * @param {Array} priceHistoryWeek - Price history for the last year
 * @param {Date} goLiveDate - Product go live date
 * @returns {Number} - Weighted average price
 */
const calculateWeightedAveragePrice = (priceHistoryWeek, goLiveDate) => {
  const oneYearBeforeGoLive = goLiveDate.clone().subtract(1, 'year');
  let totalWeightedPrice = 0;
  let totalDays = 0;

  priceHistoryWeek.forEach(({ price, effectiveDate, expiryDate }) => {
    // Calculate the number of days the price is valid for
    const validEffectiveDate = moment.max(moment(effectiveDate), oneYearBeforeGoLive); // limit to 1 year before go live date
    const validExpiryDate = moment.min(moment(expiryDate), goLiveDate); // limit to product's go live date
    const daysValid = validExpiryDate.diff(validEffectiveDate, 'days', true) + 1; // +1 to make both edges inclusive

    if (daysValid > 0) {
      totalWeightedPrice += price * daysValid;
      totalDays += daysValid;
    }
  });

  // Calculate the weighted average price
  return totalDays > 0 ? totalWeightedPrice / totalDays : 0;
};

/**
 * Calculate product sales and anchor margins from targetMargin, volume and price
 * @param {Object} product - Product containing targetMargin, volume, and price
 * @returns {Object} - Object with calculated sales, anchorMarginTotal and anchorMargin
 */
const calculateProductMargins = ({ product }) => {
  const {
    intentionPrice: { price: intentionPrice },
    priceHistoryWeek,
    mandatoryEngineInputs: {
      targetMargin,
      forecastVolume: volume,
      initialisedMargin: balancedMargin,
    },
    goLiveDate,
  } = product;

  // Take the average price from the last year before goLiveDate from priceHistoryWeek
  // If no price history is available, use the intention price
  const avgHistoricalPrice = !_.isEmpty(priceHistoryWeek)
    ? calculateWeightedAveragePrice(priceHistoryWeek, goLiveDate)
    : 0;
  const price = avgHistoricalPrice || intentionPrice;

  const sales = getSales({ volume, price });

  const anchorMarginTotal = getMarginTotal({ marginPercentage: targetMargin, sales });
  const anchorMargin = getMarginPercentage({ marginTotal: anchorMarginTotal, sales });

  const initialisedMarginTotal = getMarginTotal({ marginPercentage: balancedMargin, sales });
  const initialisedMargin = getMarginPercentage({ marginTotal: initialisedMarginTotal, sales });

  const deltaMarginTotal = anchorMarginTotal - initialisedMarginTotal;
  const deltaMargin = anchorMargin - initialisedMargin;

  const productMargins = {
    sales,
    anchorMarginTotal,
    anchorMargin,
    initialisedMarginTotal,
    initialisedMargin,
    deltaMarginTotal,
    deltaMargin,
    productCount: 1,
  };

  return productMargins;
};

/**
 * Aggregates product sales and margins for the given aggregation level
 * @param {Object} productMargins - Calculated product margins
 * @param {Object} aggregation - Aggregated margin values where calculatedMargins will be included
 * @returns {Object} - Object with calculated sales, anchorMarginTotal and anchorMargin
 */
const aggregateProductMargins = ({ productMargins, aggregation }) => {
  aggregation.productCount += productMargins.productCount;
  // sales = sum(sales)
  aggregation.sales += productMargins.sales;

  // anchorMarginTotal = sum(anchorMarginTotal)
  aggregation.anchorMarginTotal += productMargins.anchorMarginTotal;
  // anchorMargin = sum(anchorMarginTotal) / sum(sales)
  aggregation.anchorMargin =
    getMarginPercentage({
      marginTotal: aggregation.anchorMarginTotal,
      sales: aggregation.sales,
    }) || 0;

  // initialisedMarginTotal = sum(initialisedMarginTotal)
  aggregation.initialisedMarginTotal += productMargins.initialisedMarginTotal;
  // initialisedMargin = sum(initialisedMarginTotal) / sum(sales)
  aggregation.initialisedMargin =
    getMarginPercentage({
      marginTotal: aggregation.initialisedMarginTotal,
      sales: aggregation.sales,
    }) || 0;

  aggregation.deltaMarginTotal = aggregation.anchorMarginTotal - aggregation.initialisedMarginTotal;
  aggregation.deltaMargin = aggregation.anchorMargin - aggregation.initialisedMargin;

  return aggregation;
};

/**
 * Gets the margin total remainder per item based on sales weighting
 * @param {Object} salesPerItem - Map of item key to sales value
 * @param {Number} marginTotalRemainder - Margin total to be distributed across items
 * @returns {Object} - Object containing the map of item key to weighted margin total remainder
 */
const getMarginTotalRemainderPerItem = ({ salesPerItem, marginTotalRemainder }) => {
  const absoluteTotal = Object.values(salesPerItem).reduce(
    (acc, sales) => acc + Math.abs(sales),
    0
  );
  const marginTotalRemainderPerItem = Object.keys(salesPerItem).reduce((acc, col) => {
    acc[col] = (Math.abs(salesPerItem[col]) / absoluteTotal) * marginTotalRemainder || 0;
    return acc;
  }, {});

  return marginTotalRemainderPerItem;
};

/**
 * Cascades margin down from aggregated levels
 * When cascading down proportionately, apply the margin difference after the update to all children
 * When cascading down non-proportionately, apply the same margin of the update to all children
 * @param {Boolean} proportional - Cascading type, whether to cascade proportionately or not
 * @param {Number} previousMargin - Previous anchor margin of the child cell
 * @param {Number} newMargin - New anchor margin in the aggregated cell, getting surfaced to child
 * @param {Number} parentMarginDiff - Margin diff between previous and new margin in the aggregated (parent) cell
 * @returns {Number} - New margin calculated based on the cascadeDown type
 */
const cascadeDown = ({ proportional = false, previousMargin, newMargin, parentMarginDiff }) => {
  if (proportional) {
    return previousMargin + parentMarginDiff;
  }
  return newMargin;
};

module.exports = {
  calculateProductMargins,
  aggregateProductMargins,
  cascadeDown,
  getMarginTotal,
  getMarginPercentage,
  getMarginTotalRemainderPerItem,
};
